import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { API_ROOT } from "@/config";
import { AgGridReact } from "ag-grid-react";
import SEO from "@/components/seo";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Check, X } from 'lucide-react';
import FeatureRequestButton from '@/components/ui/feature-request-button';

interface Aspect {
  cat: 'visual' | 'audio';
  typ: 'dos' | 'donts';
  input: string;
  complies: boolean;
}

const getScoreColor = (score: number) => {
  if (score >= 90) return { background: 'rgba(34, 197, 94, 0.2)', foreground: '#22c55e' }; // Green: 90-100%
  if (score >= 61) return { background: 'rgba(250, 204, 21, 0.2)', foreground: '#facc15' }; // Yellow: 61-89%
  return { background: 'rgba(255, 77, 109, 0.2)', foreground: '#ff4d6d' }; // Red: 0-60%
};

const ComplianceScoreChart = ({ score }: { score: number }) => {
  const size = 150;
  const strokeWidth = 15;
  const center = size / 2;
  const radius = center - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;

  const offset = circumference - (score / 100) * circumference;
  const { background, foreground } = getScoreColor(score);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={background}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={foreground}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
      </svg>
      <span className="absolute text-4xl font-bold" style={{ color: foreground }}>
        {Math.round(score)}%
      </span>
    </div>
  );
};

interface ShareData {
  unique_id: string;
  campaignName: string;
  brandName: string;
  exactProduct: string;
  transcriptLanguage: string;
  selectedRequirements: { type: string; mode: string; requirement: string; compliance: string }[];
  comment?: string;
  email?: string;
  complianceScore?: number;
}

const languageMap: Record<string, string> = {
  de: "German",
  fr: "French",
  it: "Italian",
  es: "Spanish",
  pt: "Portuguese",
  en: "English",
};

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const VideoShare = () => {
  const { selection_id } = useParams<{ selection_id: string }>();
  const navigate = useNavigate();
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  const token = localStorage.getItem("token");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Fetch presigned URL for direct S3 access (better performance)
  useEffect(() => {
    const fetchVideoUrl = async () => {
      if (!shareData?.unique_id) return;
      
      console.log("🔗 SHARE: Fetching video URL for shared content:", shareData.unique_id);
      
      try {
        // Try authenticated access first if token exists
        if (token) {
          console.log("🔑 SHARE: Trying authenticated access");
          const response = await fetch(`${API_ROOT}/video/${shareData.unique_id}/url?token=${token}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.presigned_url) {
              console.log("✅ SHARE: Got presigned URL for direct S3 access (authenticated)");
              setVideoUrl(data.presigned_url);
              return;
            } else if (data.local_available) {
              console.log("📁 SHARE: Video available locally (authenticated)");
              setVideoUrl(`${API_ROOT}/video/${shareData.unique_id}?token=${token}`);
              return;
            }
          }
        }
        
        // Try public share link endpoints (no authentication required)
        console.log("🌐 SHARE: Trying public share link access");
        const shareResponse = await fetch(`${API_ROOT}/share/video/${shareData.unique_id}/url`, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (shareResponse.ok) {
          const shareData_response = await shareResponse.json();
          if (shareData_response.presigned_url) {
            console.log("✅ SHARE: Got presigned URL for direct S3 access (public share)");
            setVideoUrl(shareData_response.presigned_url);
            return;
          } else if (shareData_response.local_available) {
            console.log("📁 SHARE: Video available locally (public share)");
            setVideoUrl(`${API_ROOT}/share/video/${shareData.unique_id}`);
            return;
          }
        }
        
        // Final fallback - direct public share video endpoint
        console.log("🔄 SHARE: Using direct public share video endpoint");
        setVideoUrl(`${API_ROOT}/share/video/${shareData.unique_id}`);
        
      } catch (error) {
        console.error("❌ SHARE: Error fetching video URL:", error);
        console.log("🔄 SHARE: Falling back to public share video endpoint");
        setVideoUrl(`${API_ROOT}/share/video/${shareData.unique_id}`);
      }
    };
    
    fetchVideoUrl();
  }, [shareData?.unique_id, token]);

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error("Video loading error:", e);
    const videoElement = e.currentTarget;
    console.log("Video error details:", videoElement.error);

    // If S3 URL failed, try getting a new presigned URL
    if (videoElement.src.includes('amazonaws.com')) {
      console.log("🔍 SHARE DEBUG: S3 presigned URL failed, getting new presigned URL");
      
      const fetchNewPresignedUrl = async () => {
        try {
          const response = await fetch(`${API_ROOT}/video/${shareData?.unique_id}/url?token=${token}&retry=${Date.now()}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.presigned_url) {
              console.log("✅ SHARE: Got new presigned URL for retry");
              setVideoUrl(data.presigned_url);
              return;
            }
          }
        } catch (error) {
          console.error("Error fetching new presigned URL:", error);
        }
        
        // Fallback to proxied access
        console.log("🔄 SHARE: Falling back to proxied access");
        setVideoUrl(`${API_ROOT}/video/${shareData?.unique_id}?token=${token}&t=${Date.now()}`);
      };

      fetchNewPresignedUrl();
      return;
    }

    // For non-S3 URLs, retry with proxied access
    if (videoRef.current && token) {
      const newUrl = `${API_ROOT}/video/${shareData?.unique_id}?token=${token}&t=${Date.now()}`;
      console.log("Retrying video load with URL:", newUrl);
      setVideoUrl(newUrl);
    }
  };

  const handleVideoLoad = () => {
    console.log("Video loaded successfully");
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
  };

  useEffect(() => {
    if (!selection_id) return;

    try {
      const data = localStorage.getItem(`selection_${selection_id}`);
      if (data) {
        const parsedData = JSON.parse(data);
        setShareData(parsedData);
      }
    } catch (error) {
      console.error("Error loading share data:", error);
      navigate("/upload");
    }
  }, [selection_id, navigate]);

  if (!shareData) {
    return (
      <div className="flex h-screen overflow-hidden">
        <div 
          className="flex-1 flex items-center justify-center"
          style={{ background: PALETTE.brightSnow }}
        >
          <div className="text-xl font-semibold" style={{ color: PALETTE.gunmetal }}>Loading Share Data...</div>
        </div>
      </div>
    );
  }

  const tableRows = shareData.selectedRequirements || [];

  const ComplianceCell = (props: any) => (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: props.value === '✅' ? '#e6faed' : '#fee2e2',
        color: props.value === '✅' ? '#22c55e' : '#f43f5e',
        fontSize: 18,
      }}>
        {props.value === '✅' ? <Check style={{ width: 18, height: 18 }} /> : <X style={{ width: 18, height: 18 }} />}
      </span>
    </div>
  );

  const columnDefs:ColDef[]=[
    {
      headerName:'DO/DON\'T',
      field:'type',
      width: 120,
      cellStyle:{ background: '#fff', color: '#243837', fontWeight: 600, border: 'none' },
      headerClass:'custom-header'
    },
    {
      headerName:'Requirement',
      field:'requirement',
      flex:1,
      cellStyle:{ background: '#fff', color: '#243837', border: 'none' },
      headerClass:'custom-header'
    },
    {
      headerName:'Compliance',
      field:'compliance',
      width: 120,
      cellRenderer:ComplianceCell,
      cellStyle:{ textAlign: 'center', background: '#fff', border: 'none' },
      headerClass:'custom-header'
    },
  ];

  return (
    <div className="flex h-screen overflow-hidden">
      <SEO
        title="Shared Video Results"
        description="View shared BrandComply video compliance results. See how creator content matches the campaign brief."
        path={`/share/${selection_id}`}
      />
      {/* Main Content - Full width, no sidebar */}
      <div 
        className="flex-1 flex flex-col px-8 py-8 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden relative"
      >
        {/* Feature Request Button - Top Right */}
        <div className="absolute top-4 right-8 z-10">
          <FeatureRequestButton />
        </div>
        <div className="flex-grow flex flex-col justify-start pt-8 md:pt-16 px-2 sm:px-4">
          {/* Main Content Grid - Responsive Layout */}
          <div className="w-full mx-auto">
            <div className="flex flex-col xl:flex-row gap-4 xl:gap-6 justify-center">
              {/* Left Column - Video and Metadata */}
              <div className="w-full max-w-sm mx-auto xl:mx-0 xl:flex-shrink-0 xl:w-auto flex flex-col gap-4" style={{ width: window.innerWidth >= 1280 ? '360px' : '100%' }}>
                {/* Video Player */}
                {videoUrl && (
                  <Card className="p-2 bg-white rounded-lg shadow-lg border border-gray-200 h-auto">
                      <div className="relative">
                          <video
                            key={videoUrl}
                            ref={videoRef}
                            src={videoUrl}
                            controls
                            className="w-full rounded-lg"
                            style={{ aspectRatio: '9/16', objectFit: 'cover' }}
                          playsInline
                          preload="metadata"
                          onError={handleVideoError}
                          onLoadedData={handleVideoLoad}
                          onPlay={handleVideoPlay}
                          onPause={handleVideoPause}
                          onEnded={handleVideoEnded}
                        >
                          Your browser does not support the video tag.
                        </video>
                        
                                                {/* Video Metadata Overlays - Only show when video is not playing */}
                        {!isVideoPlaying && (
                          <div className="absolute top-2 right-2" style={{ marginTop: '20px' }}>
                            {/* Campaign */}
                            <div style={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(8px)',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              border: '1px solid rgba(255, 255, 255, 0.8)',
                              padding: '6px',
                              marginBottom: '6px',
                              minWidth: '200px'
                            }}>
                              <div className="px-2 py-1 text-xs bg-gray-200 rounded">
                                <strong>Campaign: </strong>{shareData?.campaignName || "Untitled"}
                              </div>
                            </div>
                            
                            {/* Language */}
                            <div style={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(8px)',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              border: '1px solid rgba(255, 255, 255, 0.8)',
                              padding: '6px',
                              marginBottom: '6px',
                              minWidth: '200px'
                            }}>
                              <div className="px-2 py-1 text-xs bg-gray-200 rounded">
                                <strong>Language: </strong>{languageMap[shareData?.transcriptLanguage] || shareData?.transcriptLanguage || "-"}
                              </div>
                            </div>
                            
                            {/* Brand */}
                            <div style={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(8px)',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              border: '1px solid rgba(255, 255, 255, 0.8)',
                              padding: '6px',
                              marginBottom: '6px',
                              minWidth: '200px'
                            }}>
                              <div className="px-2 py-1 text-xs bg-gray-200 rounded">
                                <strong>Brand: </strong>{shareData?.brandName || "-"}
                              </div>
                            </div>
                            
                            {/* Product */}
                            <div style={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(8px)',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              border: '1px solid rgba(255, 255, 255, 0.8)',
                              padding: '6px',
                              minWidth: '200px'
                            }}>
                              <div className="px-2 py-1 text-xs bg-gray-200 rounded">
                                <strong>Product: </strong>{shareData?.exactProduct || "-"}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                </Card>
              )}
            </div>

            {/* Right Column - AG Grid table - Responsive */}
            <div className="w-full xl:flex-initial xl:w-auto" style={{ width: window.innerWidth >= 1280 ? '600px' : '100%' }}>
              <div className="rounded-lg shadow-lg border border-gray-200 overflow-hidden bg-white">
                <div 
                  className="ag-theme-quartz" 
                  style={{ 
                    width: '100%', 
                    height: `${Math.max(42 + (tableRows.length * 40), 200)}px`,
                    maxHeight: window.innerWidth >= 1024 ? '642px' : '400px',
                    overflow: 'hidden'
                  }}
                >
                  <style>
                    {`
                      .ag-theme-quartz .ag-body-vertical-scroll-viewport {
                        overflow: hidden !important;
                      }
                      .ag-theme-quartz .ag-body-vertical-scroll {
                        display: none !important;
                      }
                      .ag-theme-quartz .ag-body-vertical-scroll-viewport::-webkit-scrollbar {
                        display: none !important;
                      }
                      .ag-theme-quartz .ag-body-vertical-scroll-viewport {
                        -ms-overflow-style: none !important;
                        scrollbar-width: none !important;
                      }
                    `}
                  </style>
                  <AgGridReact
                    rowData={tableRows}
                    columnDefs={columnDefs}
                    domLayout="normal"
                    headerHeight={42}
                    rowHeight={40}
                    defaultColDef={{ resizable: false }}
                    suppressMovableColumns={true}
                    suppressMenuHide={true}
                    suppressRowClickSelection={true}
                    suppressScrollOnNewData={true}
                    suppressHorizontalScroll={true}
                  />
                </div>
              </div>
            </div>

          </div>
          
          {/* Bottom Navigation - REMOVED */}
        </div>
      </div>





        <style>{`
          .ag-theme-quartz .ag-header{background:#f9fafb;border-bottom:1px solid #e5e7eb;}
          .ag-theme-quartz .ag-header-cell.custom-header{background:#f9fafb;color:#243837;font-weight:700;font-size:14px;border-right:1px solid #e5e7eb;text-align:center;}
          .ag-theme-quartz .ag-header-cell:last-child.custom-header{border-right:none;}
          .ag-theme-quartz .ag-row{border:none !important;}
          .ag-theme-quartz .ag-cell{border:none !important;font-size:14px;background:#fff;padding:0 12px;display:flex;align-items:center;}
          .ag-theme-quartz .ag-row-hover .ag-cell,.ag-theme-quartz .ag-row:hover .ag-cell{background:#f0f1f3 !important;}
          .ag-theme-quartz .ag-header-cell-text{overflow:visible;white-space:nowrap;}
        `}</style>
      </div>
    </div>
  );
};

export default VideoShare;
